import { Box, BoxProps } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import * as React from 'react';
import {
  FieldValues,
  FormProvider,
  Mode,
  Path,
  UseFormReturn,
  UseFormSetError,
  useForm,
} from 'react-hook-form';
import { AnyObjectSchema } from 'yup';

export type FormInstanceType<TValues extends FieldValues> =
  UseFormReturn<TValues> & {
    submit: () => void;
  };

interface FormProps<TValues extends FieldValues>
  extends Omit<BoxProps, 'onSubmit' | 'children'> {
  validationSchema?: AnyObjectSchema;
  defaultValues?: Record<string, any>;
  onSubmit?(
    values: TValues,
    setError: UseFormSetError<TValues>
  ): Promise<any> | void;
  children:
    | ((
        props: UseFormReturn<TValues> & { submit: () => Promise<any> }
      ) => React.ReactNode)
    | React.ReactNode;
  mode?: Mode;
  shouldTouchOnSubmit?: boolean;
  innerRef?: React.RefObject<FormInstanceType<TValues>>;
}

const Form = <TValues extends FieldValues = FieldValues>({
  validationSchema,
  defaultValues,
  onSubmit,
  children,
  innerRef,
  mode = 'onBlur',
  shouldTouchOnSubmit = true,
  ...rest
}: FormProps<TValues>): React.ReactElement => {
  const formProviderProps = useForm<TValues>({
    defaultValues: defaultValues as any,
    ...(validationSchema && {
      resolver: (yupResolver as any)(validationSchema),
    }),
    mode,
  });

  const handleSubmit = formProviderProps.handleSubmit(
    (data: any) => {
      if (!onSubmit) {
        return Promise.resolve();
      }
      return onSubmit(data, formProviderProps.setError);
    },
    (errors) => {
      if (!shouldTouchOnSubmit) {
        return;
      }

      const validationFailedKeys = Object.keys(errors) as (keyof TValues)[];
      const { setValue, getValues } = formProviderProps;
      const formValues = getValues();
      validationFailedKeys.forEach((key) => {
        setValue(key as Path<TValues>, formValues[key], { shouldTouch: true });
      });
    }
  );

  React.useImperativeHandle(
    innerRef,
    () => ({ ...formProviderProps, submit: handleSubmit }),
    [formProviderProps, handleSubmit]
  );

  return (
    <FormProvider {...(formProviderProps as any)}>
      <Box as="form" noValidate onSubmit={handleSubmit} {...rest}>
        {typeof children === 'function'
          ? children({ ...formProviderProps, submit: handleSubmit })
          : children}
      </Box>
    </FormProvider>
  );
};

export default Form;
